import React, { ReactElement, useCallback } from 'react';
import Editor, {
    BtnBold,
    BtnBulletList,
    BtnItalic,
    BtnNumberedList,
    BtnUnderline,
    ContentEditableEvent,
    Separator,
    Toolbar,
} from 'react-simple-wysiwyg';
import DOMPurify from 'dompurify';

export default function WysiwygEditor(props: {
    bold?: boolean;
    italic?: boolean;
    onChange: (content: string) => void;
    orderedList?: boolean;
    underline?: boolean;
    unorderedList?: boolean;
    value: string;
}): ReactElement {
    const {
        bold = false,
        italic = false,
        onChange,
        orderedList = false,
        underline = false,
        unorderedList = false,
        value,
    } = props;

    const initialContent = (!value || value.trim() === '') ? '<p><br></p>' : value;

    const onChangeEditorContent: (content: ContentEditableEvent) => void = useCallback((content: ContentEditableEvent) => {
        // Sanitise to remove any styles added while copy - pasting
        let cleanHtml: string = DOMPurify.sanitize(content.target.value, {
            ALLOWED_TAGS: [
                'b', 'i', 'u', 'p', 'ul', 'ol', 'li', 'br', 'strong', 'em', 'div',
            ],
            ALLOWED_ATTR: [],
        });

        // Remove only <p></p> tags with no spaces inside, since they are added randomly.
        cleanHtml = cleanHtml.replace(/<p><\/p>/g, '');

        onChange(cleanHtml);
    }, [
        onChange,
    ]);

    const hasInlineFormatting: boolean = bold || italic || underline;
    const hasListFormatting: boolean = unorderedList || orderedList;

    const toolbarBlock: JSX.Element = (hasInlineFormatting || hasListFormatting) && (
        <Toolbar>
            {bold && <BtnBold />}
            {italic && <BtnItalic />}
            {underline && <BtnUnderline />}
            {hasInlineFormatting && hasListFormatting && <Separator />}
            {unorderedList && <BtnBulletList />}
            {orderedList && <BtnNumberedList />}
        </Toolbar>
    );

    return (
        <Editor
            containerProps={{ className: 'wysiwyg-editor' }}
            onChange={onChangeEditorContent}
            value={initialContent}
        >
            {toolbarBlock}
        </Editor>
    );
}
